import type { ReactNode } from "react";
import type { RadioGroupProps } from "react-aria-components";
import { Text, FieldError, RadioGroup as RadioGroupBase, Label, Radio } from "react-aria-components";
import { css, cva, cx } from "../../../../styled-system/css";
import { formField } from "../../../../styled-system/recipes";

interface Props extends Omit<RadioGroupProps, "children"> {
  items?: { label: ReactNode; value: string; isDisabled?: boolean }[];
  label?: string;
  description?: string;
  errorMessage?: string;
  className?: string;
  justify?: "around" | "start";
}

const radioStyles = css({
  cursor: "pointer",

  display: "flex",
  gap: "xsmall",
  alignItems: "center",
  fontSize: "medium",
  _disabled: {
    opacity: 0.5,
  },
  _before: {
    boxSizing: "border-box",
    display: "block",
    border: "1px solid {colors.royalPurple}",
    borderRadius: "full",
    width: "{fontSizes.large}",
    height: "{fontSizes.large}",
    transition: "all 200ms",
    content: "''",
  },

  "&[data-selected]": {
    "&:before": {
      borderWidth: "5px",
    },
  },

  "&[data-focus-visible]:before": {
    outline: "2px solid {colors.trellisPalette.1}",
    outlineOffset: "2px",
  },

  "&[data-invalid]": {
    "&:before": {
      borderColor: "error",
    },
  },
});

export function RadioGroup({
  label,
  description,
  errorMessage,
  items,
  className,
  justify = "around",
  ...props
}: Props) {
  const classes = formField();
  return (
    <RadioGroupBase {...props} className={cx(classes.fieldWrapper, className)}>
      {({ orientation }) => (
        <>
          {label && <Label className={classes.label}>{label}</Label>}
          <div
            className={cva({
              base: {
                display: "flex",
                gap: "small",
                minHeight: "",
              },
              variants: {
                orientation: {
                  vertical: {
                    flexDirection: "column",
                  },
                  horizontal: {
                    flexDirection: "row",
                    height: "39px",
                  },
                },
                justify: {
                  around: {},
                  start: {},
                },
              },
              compoundVariants: [
                {
                  orientation: "horizontal",
                  justify: "around",
                  css: { justifyContent: "space-around" },
                },
                {
                  orientation: "horizontal",
                  justify: "start",
                  css: { justifyContent: "flex-start", gap: "medium" },
                },
              ],
            })({ orientation, justify })}
          >
            {items?.map((item) => (
              <Radio key={item.value} value={item.value} isDisabled={item.isDisabled} className={radioStyles}>
                {item.label}
              </Radio>
            ))}
          </div>
          {description && <Text slot="description">{description}</Text>}
          {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
        </>
      )}
    </RadioGroupBase>
  );
}
